import React, { useEffect, useState } from "react";
import AdminWrapper from "../../../components/AdminWrapper";
import { useDispatch, useSelector } from "react-redux";
import { GetAdmin } from "../../../redux/reducer/GetAdminDetail";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Close,
  Download,
  Refresh,
  Email,
  Phone,
  LocationOn,
  Badge,
  CalendarMonth,
} from "@mui/icons-material";
import {
  Person,
  Favorite,
  Work,
  Payments,
  VerifiedUser,
  Home,
} from "@mui/icons-material";

const TenantManagement = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { loading, GetAdminDetail } = useSelector(
    (state) => state.getAdminDetail
  );
  const stats = GetAdminDetail;

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    dispatch(GetAdmin());
  }, [dispatch]);

  const tenants = stats?.tenant_detail || [];

  // Filter tenants
  const filteredTenants = tenants.filter((tenant) => {
    const matchesSearch =
      (tenant.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (tenant.email?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (tenant.mobileNumber?.toLowerCase() || "").includes(searchQuery.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && tenant.is_active) ||
      (filterStatus === "inactive" && !tenant.is_active);

    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = (tenant) => {
    setSelectedTenant(tenant);
    setDetailsOpen(true);
  };

  const activeCount = tenants.filter((t) => t.is_active).length;
  const inactiveCount = tenants.filter((t) => !t.is_active).length;

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Skeleton variant="text" width={220} height={40} />
          <Skeleton variant="text" width={180} height={20} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={100} height={36} />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={6} sm={4} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Skeleton
                  variant="text"
                  width={60}
                  height={45}
                  sx={{ mx: "auto" }}
                />
                <Skeleton
                  variant="text"
                  width={80}
                  height={20}
                  sx={{ mx: "auto" }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Skeleton
        variant="rounded"
        width="100%"
        height={56}
        sx={{ mb: 2, borderRadius: 2 }}
      />
      <Skeleton
        variant="rounded"
        width="100%"
        height={400}
        sx={{ borderRadius: 2 }}
      />
    </>
  );

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "name",
      headerName: "Tenant",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <Avatar
            src={params.row.profilePhoto || ""}
            alt={params.row.name}
            sx={{
              width: 42,
              height: 42,
              bgcolor: "#f59e0b20",
              color: "#f59e0b",
            }}
          >
            {(params.row.name || "T").charAt(0).toUpperCase()}
          </Avatar>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {params.row.name || "N/A"}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "mobileNumber",
      headerName: "Contact",
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Phone sx={{ fontSize: 16, color: "#9ca3af" }} />
          <Typography variant="body2">{params.value || "N/A"}</Typography>
        </Box>
      ),
    },
    {
      field: "cnic",
      headerName: "CNIC",
      width: 150,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={
            params.value ? (
              <CheckCircle sx={{ fontSize: 14 }} />
            ) : (
              <Cancel sx={{ fontSize: 14 }} />
            )
          }
          label={params.value ? "Active" : "Inactive"}
          size="small"
          sx={{
            bgcolor: params.value ? "#dcfce7" : "#fee2e2",
            color: params.value ? "#166534" : "#991b1b",
            fontWeight: 500,
            "& .MuiChip-icon": { color: "inherit" },
          }}
        />
      ),
    },
    {
      field: "date",
      headerName: "Joined",
      width: 120,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "N/A",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row)}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#1e293b" }}
                >
                  Tenant Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and manage all tenants on the platform
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => dispatch(GetAdmin())}
                  sx={{ borderColor: "#e5e7eb", color: "#64748b" }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}
                >
                  Export
                </Button>
              </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4}>
                <Card sx={{ borderRadius: 2, border: "1px solid #e5e7eb" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#f59e0b" }}
                    >
                      {tenants.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Tenants
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Card sx={{ borderRadius: 2, border: "1px solid #86efac" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#22c55e" }}
                    >
                      {activeCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ borderRadius: 2, border: "1px solid #fca5a5" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#ef4444" }}
                    >
                      {inactiveCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactive
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name, email, or contact..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: "#9ca3af" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Status"
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setSearchQuery("");
                      setFilterStatus("all");
                    }}
                    sx={{ borderColor: "#e5e7eb" }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Data Table */}
            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
              <DataGrid
                rows={filteredTenants}
                columns={columns}
                getRowId={(row) => row._id || Math.random()}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "#f8fafc",
                    borderBottom: "1px solid #e5e7eb",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                    color: "#374151",
                  },
                  "& .MuiDataGrid-row:hover": {
                    bgcolor: "#f8fafc",
                  },
                }}
              />
            </Paper>

            {/* Tenant Details Dialog */}
            <Dialog
              open={detailsOpen}
              onClose={() => setDetailsOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Tenant Details
                </Typography>
                <IconButton onClick={() => setDetailsOpen(false)}>
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {selectedTenant && (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Avatar
                        src={
                          selectedTenant.profileImage ||
                          selectedTenant.profilePhoto
                        }
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: "#f59e0b20",
                          color: "#f59e0b",
                          fontSize: 32,
                        }}
                      >
                        {(selectedTenant.name || "T").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {selectedTenant.name || "N/A"}
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            selectedTenant.is_active ? "Active" : "Inactive"
                          }
                          sx={{
                            bgcolor: selectedTenant.is_active
                              ? "#dcfce7"
                              : "#fee2e2",
                            color: selectedTenant.is_active
                              ? "#166534"
                              : "#991b1b",
                            mt: 0.5,
                          }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Email sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Email
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.email || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Phone sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Contact
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.mobileNumber || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Badge sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              CNIC
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.cnic || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <CalendarMonth sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Joined Date
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.date
                                ? new Date(
                                    selectedTenant.date
                                  ).toLocaleDateString()
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Badge sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Father Name
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.fatherName || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Person sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Gender
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.gender || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Favorite sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Marital Status
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.maritalStatus || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Work sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Employment
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.employmentStatus || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <Payments sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Monthly Income
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.monthlyIncome || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <VerifiedUser sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Registration
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.is_register
                                ? "Registered"
                                : "Not Registered"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            mb: 2,
                          }}
                        >
                          <LocationOn sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Current Address
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.currentAddress || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                          }}
                        >
                          <Home sx={{ color: "#6366f1" }} />
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Permanent Address
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedTenant.permanentAddress || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button
                  onClick={() => setDetailsOpen(false)}
                  variant="outlined"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </>
  );
};

export default TenantManagement;
